import { useEffect, Fragment, useState } from "react";
import { ArrowUp, ArrowDown, Minus, CaretDown, CaretRight } from "@phosphor-icons/react";
import { getLineMovementDetail } from "../../lib/api";

const formatGameTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const formatRelative = (iso) => {
  if (!iso) return "--";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.max(0, Math.round(diffMs / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

const formatPrice = (price) => (price === null || price === undefined ? "--" : price > 0 ? `+${price}` : `${price}`);

const MovementBadge = ({ movement, direction }) => {
  if (movement === null || movement === undefined) {
    return <span className="font-mono text-xs text-zinc-600">--</span>;
  }
  const Icon = direction === "up" ? ArrowUp : direction === "down" ? ArrowDown : Minus;
  const color = direction === "up" ? "text-emerald-400" : direction === "down" ? "text-red-400" : "text-zinc-500";
  const sign = movement > 0 ? "+" : "";
  return (
    <span className={`flex items-center gap-1 font-mono text-xs font-semibold ${color}`}>
      <Icon size={12} weight="bold" />
      {sign}{movement.toFixed(1)}
    </span>
  );
};

const BookRow = ({ book }) => (
  <tr data-testid={`line-movement-book-row-${book.bookmaker}`} className="border-b border-white/5 text-zinc-300 last:border-0">
    <td className="px-3 py-2 capitalize">{book.bookmaker}</td>
    <td className="px-3 py-2">{book.current.total_line}</td>
    <td className="px-3 py-2">{formatPrice(book.current.over_price)}</td>
    <td className="px-3 py-2">{formatPrice(book.current.under_price)}</td>
    <td className="px-3 py-2 text-zinc-500">
      {book.history.length > 1 ? book.history.map((h) => h.total_line).join(" \u2192 ") : "No movement yet"}
    </td>
  </tr>
);

const ExpandedDetail = ({ eventId }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getLineMovementDetail(eventId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [eventId]);

  if (loading) return <p className="px-3 py-3 font-mono text-xs text-zinc-500">Loading bookmaker breakdown...</p>;
  if (!detail || !detail.books || detail.books.length === 0) {
    return <p className="px-3 py-3 font-mono text-xs text-zinc-600">No bookmaker data currently available for this game.</p>;
  }

  return (
    <div data-testid={`line-movement-expanded-${eventId}`} className="overflow-x-auto p-3">
      <table className="w-full font-mono text-xs">
        <thead>
          <tr className="border-b border-white/10 text-left text-zinc-500">
            <th className="px-3 py-2">Book</th>
            <th className="px-3 py-2">Total</th>
            <th className="px-3 py-2">Over</th>
            <th className="px-3 py-2">Under</th>
            <th className="px-3 py-2">Tracked Movement</th>
          </tr>
        </thead>
        <tbody>
          {detail.books.map((book) => (
            <BookRow key={book.bookmaker} book={book} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const MovementTable = ({ rows }) => {
  const [expanded, setExpanded] = useState(null);

  if (rows.length === 0) {
    return (
      <p data-testid="line-movement-table-empty" className="rounded-lg border border-dashed border-zinc-800 bg-eb-surface/40 px-6 py-10 text-center font-mono text-xs text-zinc-600">
        No tracked pregame totals match this filter yet.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-white/10 bg-eb-surface">
      <table data-testid="line-movement-table" className="w-full font-mono text-xs">
        <thead>
          <tr className="border-b border-white/10 text-left text-zinc-500">
            <th className="px-3 py-2"></th>
            <th className="px-3 py-2">League</th>
            <th className="px-3 py-2">Matchup</th>
            <th className="px-3 py-2">Game Time</th>
            <th className="px-3 py-2">Tracked Open</th>
            <th className="px-3 py-2">Current</th>
            <th className="px-3 py-2">Movement</th>
            <th className="px-3 py-2">Books</th>
            <th className="px-3 py-2">Last Update</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOpen = expanded === r.event_id;
            return (
              <Fragment key={r.event_id}>
                <tr
                  data-testid={`line-movement-row-${r.event_id}`}
                  onClick={() => setExpanded(isOpen ? null : r.event_id)}
                  className="cursor-pointer border-b border-white/5 text-zinc-300 transition-colors last:border-0 hover:bg-white/[0.03]"
                >
                  <td className="px-3 py-2 text-zinc-500">
                    {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                  </td>
                  <td className="px-3 py-2 uppercase text-zinc-400">{r.league}</td>
                  <td className="px-3 py-2 text-white">{r.away_team} @ {r.home_team}</td>
                  <td className="px-3 py-2 text-zinc-500">{formatGameTime(r.commence_time)}</td>
                  <td className="px-3 py-2">{r.tracked_open_total ?? "--"}</td>
                  <td className="px-3 py-2">{r.current_total ?? "--"}</td>
                  <td className="px-3 py-2" data-testid={`line-movement-row-${r.event_id}-movement`}>
                    <MovementBadge movement={r.movement} direction={r.movement_direction} />
                  </td>
                  <td className="px-3 py-2 text-zinc-500">{r.current_books_count}</td>
                  <td className="px-3 py-2 text-zinc-500">
                    {r.status === "started" ? "Started" : formatRelative(r.last_updated_at)}
                  </td>
                </tr>
                {isOpen && (
                  <tr className="border-b border-white/5 bg-eb-bg/60 last:border-0">
                    <td colSpan={9}>
                      <ExpandedDetail eventId={r.event_id} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
